/* eslint-disable @next/next/no-img-element */
import { cn } from '@/lib/utils'

/**
 * Certification badge: the supplied ISO wordmark on the footer's tile.
 *
 * The mark is a file rather than drawn inline. Both wordmarks ship with the
 * same internal element ids (`mask`, `katman_1`, …), so inlining them together
 * would put duplicate ids in one document and the second mask would resolve
 * against the first — one badge silently renders wrong. Loading each through
 * `<img>` keeps them in separate documents, where those ids cannot collide.
 *
 * The trade-off is colour: an `<img>` cannot inherit `currentColor`, and these
 * files hardcode a single slate fill. Dark mode is therefore handled with a
 * filter. That is normally a poor way to recolour artwork, but it is safe here
 * precisely because the mark is monochrome — there is only one ink to move, so
 * it lands on white rather than flattening a multi-tone image.
 */

/** Wordmark per standard. Keys are what the footer passes as `standard`;
    w/h are each file's intrinsic pixels so the img can declare its aspect
    ratio and the browser reserves the box before the file arrives. */
const MARKS: Record<string, { src: string; w: number; h: number }> = {
  '9001': { src: '/certifications/iso-9001-wordmark.svg', w: 53, h: 48 },
  '27001': { src: '/certifications/iso-27001-wordmark.svg', w: 59, h: 48 },
}

export function CertificationBadge({
  standard,
  title,
  className,
}: {
  /** Standard number, e.g. "27001". Must have an entry in MARKS. */
  standard: string
  /** Full accessible name, e.g. "ISO 27001:2022 certified — Information Security". */
  title: string
  className?: string
}) {
  const mark = MARKS[standard]
  if (!mark) return null

  return (
    <div
      className={cn(
        'flex size-12 shrink-0 items-center justify-center rounded-2xl',
        'bg-zinc-100 dark:bg-white/[0.08]',
        className
      )}
    >
      <img
        src={mark.src}
        alt={title}
        width={mark.w}
        height={mark.h}
        loading="lazy"
        decoding="async"
        // The wordmarks are ~1.1:1 to ~1.24:1, so height drives the size and
        // width follows — otherwise 9001 and 27001 would be set to the same
        // width and the longer number would come out visibly smaller.
        className="h-7 w-auto dark:opacity-[0.85] dark:brightness-0 dark:invert"
      />
    </div>
  )
}
